import { useContext, useEffect, useState } from "react";
import { Props } from "./AccordionDetailsProps";
import MainContext from "../../../context/mainContext";
import { ISummaryGeneralInformationModel } from "../../../models/summary.model";
import summaryService from "../../../services/summary.service";
import SpinnerArea from "../../../components/SpinnerArea";
import { Box, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "./AccordionDetails.module.scss";
import Workflow from "../../workflow";
import DocumentView from "../../../components/DocumentView";

const SummaryGeneralInformation: React.FC<Props> = ({
  publicationId,
  expanded,
}) => {
  const { service } = useContext(MainContext);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generalInfo, setGeneralInfo] =
    useState<ISummaryGeneralInformationModel>();
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;

    if (active && expanded && !loaded) {
      service(() => summaryService.generalInformation(publicationId), {
        loader: setLoading,
        callback: (data) => {
          setGeneralInfo(data);
          setLoaded(true);
        },
      });
    }

    return () => {
      active = false;
    };
  }, [expanded, loaded, publicationId, service]);

  return (
    <SpinnerArea loading={loading}>
      {generalInfo && (
        <Box className={styles.generalInformation}>
          <GeneralInfoTexField propName="title" generalInfo={generalInfo} />
          <GeneralInfoTexField propName="author" generalInfo={generalInfo} />
          <GeneralInfoTexField
            propName="coAuthorsInternal"
            generalInfo={generalInfo}
          />
          <GeneralInfoTexField
            propName="coAuthorsExternal"
            generalInfo={generalInfo}
          />
          <GeneralInfoTexField propName="topics" generalInfo={generalInfo} />
          <GeneralInfoTexField
            propName="languages"
            generalInfo={generalInfo}
            label={"mainLanguages"}
            customValue={() => {
              var languages = generalInfo.languages.split(", ");

              return languages.map((x) => t(`languages.${x}`)).join(", ");
            }}
          />
          <GeneralInfoTexField propName="docType" generalInfo={generalInfo} />
          <GeneralInfoTexField
            propName={
              generalInfo?.leadingDivisions
                ? "leadingDivisions"
                : "leadingDepartments"
            }
            generalInfo={generalInfo}
          />
          <GeneralInfoTexField
            propName={
              generalInfo?.leadingDivisions
                ? "otherDivisionsInvolved"
                : "otherDepartmentsInvolved"
            }
            generalInfo={generalInfo}
          />
          <GeneralInfoTexField propName="countries" generalInfo={generalInfo} />
          <GeneralInfoTexField
            propName="abstract"
            label={"description"}
            generalInfo={generalInfo}
            customClassName={styles.abstract}
            multiline
          />
          {generalInfo.workflowReason && (
            <GeneralInfoTexField
              propName="workflowReason"
              label={
                generalInfo.workflowType !== "Delete"
                  ? "justificationOfTheModification"
                  : "justificationOfTheDeletion"
              }
              generalInfo={generalInfo}
              customClassName={styles.workflowReason}
              multiline
            />
          )}
          {generalInfo.conceptNoteDocument && (
            <div className={styles.conceptNoteDocument}>
              <label>Download Concept Note</label>
              <DocumentView document={generalInfo.conceptNoteDocument} hideOpenOnline={true} />
          </div>
          )}
        </Box>
      )}
    </SpinnerArea>
  );
};

interface GeneralInfoTexFieldProps {
  propName: keyof ISummaryGeneralInformationModel;
  generalInfo: ISummaryGeneralInformationModel;
  customClassName?: string | null;
  label?: string | null;
  multiline?: boolean | undefined;
  customValue?: () => string;
}

const GeneralInfoTexField: React.FC<GeneralInfoTexFieldProps> = ({
  propName,
  generalInfo,
  customClassName,
  label,
  multiline,
  customValue,
}) => {
  const { t } = useTranslation();

  return (
    <TextField
      id={propName}
      label={t(label || propName)}
      InputProps={{
        readOnly: true,
      }}
      InputLabelProps={{
        shrink: true,
      }}
      variant="standard"
      value={customValue ? customValue() : generalInfo[propName] || " "}
      fullWidth
      className={customClassName || ""}
      focused={false}
      multiline={multiline}
    />
  );
};

export default SummaryGeneralInformation;
